/**
 * Challenges' own vocabulary, mapped onto the panel-wide design tokens in
 * component/common/designSystem/theme.js.
 */
import {
  CHART_COLORS,
  toneChip,
  toneColor,
  toneColors,
  toneLookup
} from '../../../../component/common/designSystem';

/**
 * Where a challenge sits on the calendar. Running is the one that matters, so
 * it takes the brand colour; Ended is neutral rather than red — a finished
 * challenge is a success, not a failure.
 */
const phaseTone = toneLookup({
  running: 'success',
  upcoming: 'info',
  ended: 'neutral',
  draft: 'warning'
});

export const challengePhaseColor = (phase) => toneColor(phaseTone(phase));
export const challengePhaseChipClass = (phase) => toneChip(phaseTone(phase));

/** Phase donut — declaration order of PHASE_LABELS: draft, upcoming, running, ended. */
export const PHASE_COLORS = toneColors(['warning', 'info', 'success', 'neutral']);

/** Which app feature a challenge task asks for — categorical, no ordering. */
export const FEATURE_COLORS = toneColors(['brand', 'accent', 'teal', 'orange', 'info']);

/** Which tracker a challenge reads — steps, calories, distance, … */
export const TRACKER_COLORS = toneColors(['brand', 'teal', 'orange', 'accent', 'info']);

/**
 * Ordered ramps for the distribution charts. Each is a sequence rather than a
 * set of categories, so it gets a ramp instead of the categorical palette —
 * and each has exactly as many colours as its band list has bands, so a slice
 * never borrows its neighbour's meaning.
 */

// Lifetime tracked volume per person, Nothing tracked → 150k+.
export const VOLUME_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

// One person on one day, Under 2k → 12k+.
export const DAILY_VOLUME_RAMP = [
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

/**
 * How close a day came to its target, Under 25% → Target hit. The bottom band
 * is amber rather than red: a short day is a short day, and the chart exists
 * to show how near people got, not to mark them wrong.
 */
export const ACHIEVEMENT_RAMP = [
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.green
];

// Days with any tracked activity, No active days → 15+ days.
export const ACTIVE_DAYS_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

// Repeat participation, 1 challenge → 5+ challenges.
export const REPEAT_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

// Share of a challenge's tasks finished, No tasks done → All tasks.
export const TASK_PROGRESS_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.green
];
